import type { CollectionKey, ConstellationKey } from '@toolproof-core/visualization/_contract/key';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    buildWorkflowStateEntryConnectorConstellationKey,
    parseWorkflowStateEntryConnectorCollectionKey,
    parseWorkflowStateEntryConnectorItemKey,
} from '@/workflow/_pure/keyCodec';
import { resolveOperationPresentationMode } from '@/workflow-runner/_pure/config/config';
import type { Config } from '@/workflow-runner/_contract/config';

const SPLIT_SUFFIX = ':wheel-link:';

export function applyWorkflowStateEntryConnectorVisibility(args: {
    frame: SpaceDerivationFrame;
    config: Config;
    transformedBooleanStepKeys: ReadonlySet<string>;
}): SpaceDerivationFrame {
    if (resolveOperationPresentationMode(args.config) !== 'wheels') return args.frame;
    const { frame } = args;
    const settings = args.config.adapterModeConfig.workflowStateEntryConnectors;
    if (!settings.hideInWheelsMode && !settings.hideForTransformedBooleanOperations
        && !settings.hideIncomingForTransformedBooleanOperations
        && !frame.itemCollections.some((collection) => collection.itemCollectionKey.includes(SPLIT_SUFFIX))) return frame;
    const collections = [...frame.itemCollections];
    let constellations = [...frame.constellations];
    const states = { ...frame.constellationParametricState?.connectorConstellationByKey };
    // Keep original declarations as the source of endpoint reanchoring and authored visibility.
    // Singleton rendering constellations prevent unrelated links in the same group from disappearing.
    for (const collection of frame.itemCollections) {
        if (collection.itemCollectionKind !== 'connector') continue;
        const scope = parseWorkflowStateEntryConnectorCollectionKey(collection.itemCollectionKey);
        if (!scope) continue;
        const originalKey = buildWorkflowStateEntryConnectorConstellationKey(scope);
        const originalState = states[originalKey];
        constellations = constellations.filter((constellation) => constellation.constellationKey !== originalKey);
        for (const item of collection.items) {
            const suffix = `${SPLIT_SUFFIX}${encodeURIComponent(item.itemKey)}`;
            const collectionKey = `${collection.itemCollectionKey}${suffix}` as CollectionKey<'connector'>;
            const constellationKey = `${originalKey}${suffix}` as ConstellationKey<'connector'>;
            if (!collections.some((candidate) => candidate.itemCollectionKey === collectionKey)) {
                collections.push({ ...collection, itemCollectionKey: collectionKey, items: [item] });
                constellations.push({
                    constellationKind: 'connector', constellationKey, itemCollectionKey: collectionKey,
                });
            } else {
                const index = collections.findIndex((candidate) => candidate.itemCollectionKey === collectionKey);
                collections[index] = { ...collection, itemCollectionKey: collectionKey, items: [item] };
            }
            const address = parseWorkflowStateEntryConnectorItemKey(item.itemKey);
            if (!address) throw new Error(`Workflow-state entry connector '${item.itemKey}' has no role address.`);
            const involvesGraph = args.transformedBooleanStepKeys.has(address.sourceStepKey)
                || args.transformedBooleanStepKeys.has(address.targetStepKey);
            const hidden = settings.hideInWheelsMode
                || (settings.hideForTransformedBooleanOperations && involvesGraph)
                || (settings.hideIncomingForTransformedBooleanOperations
                    && args.transformedBooleanStepKeys.has(address.targetStepKey));
            states[constellationKey] = {
                ...originalState,
                visibility: { ...originalState?.visibility, visible: hidden ? false : originalState?.visibility?.visible ?? true },
            };
        }
    }
    return {
        ...frame, itemCollections: collections, constellations,
        constellationParametricState: { ...frame.constellationParametricState, connectorConstellationByKey: states },
    };
}
